import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Clock, ArrowRight } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { BRAND, SERVICES, SERVICE_AREAS } from "@/data/site";

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export function Footer() {
  return (
    <footer className="bg-foreground text-background">
      <div className="container-page py-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="mb-4">
            <Logo variant="light" />
          </div>
          <p className="text-background/70 text-sm leading-relaxed mb-5">
            Residential and commercial flooring installation, replacement and renovation across Connecticut. Locally owned, licensed and insured.
          </p>
          <div className="flex items-center gap-3">
            <a href={BRAND.social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
              className="h-9 w-9 rounded-full border border-background/20 flex items-center justify-center hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors">
              <Instagram className="h-4 w-4" />
            </a>
            <a href={BRAND.social.google} target="_blank" rel="noopener noreferrer" aria-label="Google"
              className="h-9 w-9 rounded-full border border-background/20 flex items-center justify-center hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors">
              <GoogleIcon className="h-4 w-4" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-accent mb-4">Services</h3>
          <ul className="space-y-2 text-sm">
            {SERVICES.slice(0, 7).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="text-background/70 hover:text-accent transition-colors">{s.name}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-accent mb-4">Service Areas</h3>
          <ul className="space-y-2 text-sm">
            {SERVICE_AREAS.slice(0, 6).map((a) => (
              <li key={a.slug}>
                <Link to={`/service-areas/${a.slug}`} className="text-background/70 hover:text-accent transition-colors">{a.name}, {a.state}</Link>
              </li>
            ))}
          </ul>
          <Link to="/service-areas" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-accent hover:underline">
            View all service areas
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-accent mb-4">Contact</h3>
          <ul className="space-y-3 text-sm text-background/80">
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 text-accent shrink-0" />
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="hover:text-accent transition-colors">{BRAND.phoneDisplay}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-accent shrink-0" />
              <a href={`mailto:${BRAND.email}`} className="hover:text-accent transition-colors break-all">{BRAND.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-accent shrink-0" />
              <span>{BRAND.address.street}, {BRAND.address.city}, {BRAND.address.region} {BRAND.address.postalCode}</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="h-4 w-4 mt-0.5 text-accent shrink-0" />
              <div>
                {BRAND.hours.map((h) => (
                  <div key={h.day}><span className="font-medium text-background">{h.day}:</span> {h.time}</div>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-background/10">
        <div className="container-page py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-background/60">
          <div>© {new Date().getFullYear()} {BRAND.legalName}. All rights reserved. Licensed & Insured.</div>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-accent transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-accent transition-colors">Terms & Conditions</Link>
          </div>
        </div>
        <div className="container-page pb-6 -mt-3 text-center md:text-right text-xs text-background/60">
          Created by{" "}
          <a href="https://2getherpro.com/" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors font-medium">2GetherPRO</a>
        </div>
      </div>
    </footer>
  );
}
